import { useEffect, useState } from 'react'

/**
 * Legacy analog clock — Braun / Rams / airport-terminal influence.
 * 60 tick marks (12 heavier), no numerals, tapered hands with pivoted counterweight
 * on the seconds hand. Face is off-white with subtle inner shading; hands are
 * charcoal-ink; the seconds hand is a soft blue (pulse) to match the design system.
 */
export default function AnalogClock({ size = 168 }: { size?: number }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  const seconds = now.getSeconds() + now.getMilliseconds() / 1000
  const minutes = now.getMinutes() + seconds / 60
  const hours = (now.getHours() % 12) + minutes / 60

  const secondAngle = seconds * 6            // 360/60
  const minuteAngle = minutes * 6
  const hourAngle = hours * 30               // 360/12

  const cx = 100
  const cy = 100
  const r = 92

  const ticks = Array.from({ length: 60 }, (_, i) => {
    const angle = (i * 6 - 90) * (Math.PI / 180)
    const isHour = i % 5 === 0
    const outerR = r - 2
    const innerR = isHour ? r - 12 : r - 6
    return {
      x1: cx + Math.cos(angle) * outerR,
      y1: cy + Math.sin(angle) * outerR,
      x2: cx + Math.cos(angle) * innerR,
      y2: cy + Math.sin(angle) * innerR,
      w: isHour ? 1.5 : 0.75,
    }
  })

  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      role="img"
      aria-label={`Analog clock showing ${now.toLocaleTimeString()}`}
    >
      <defs>
        <radialGradient id="face" cx="50%" cy="45%" r="60%">
          <stop offset="0%"  stopColor="#fefeff" />
          <stop offset="70%" stopColor="#f5f7fa" />
          <stop offset="100%" stopColor="#e6ebf0" />
        </radialGradient>
        <filter id="softshadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.12" />
        </filter>
      </defs>

      {/* Face */}
      <circle cx={cx} cy={cy} r={r} fill="url(#face)" filter="url(#softshadow)" />
      {/* Ring */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#dde3ea" strokeWidth="0.5" />

      {/* 60 tick marks — hours heavier */}
      {ticks.map((t, i) => (
        <line
          key={i}
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke="#1a1f2e" strokeWidth={t.w} strokeLinecap="round"
        />
      ))}

      {/* Hour hand — pear-shaped taper */}
      <g transform={`rotate(${hourAngle} ${cx} ${cy})`}>
        <line x1={cx} y1={cy + 8} x2={cx} y2={cy - 50}
              stroke="#1a1f2e" strokeWidth="5.5" strokeLinecap="round" />
      </g>

      {/* Minute hand — longer, thinner taper */}
      <g transform={`rotate(${minuteAngle} ${cx} ${cy})`}>
        <line x1={cx} y1={cy + 12} x2={cx} y2={cy - 74}
              stroke="#1a1f2e" strokeWidth="3.5" strokeLinecap="round" />
      </g>

      {/* Second hand — thin soft-blue with counterweight tail */}
      <g transform={`rotate(${secondAngle} ${cx} ${cy})`}>
        <line x1={cx} y1={cy + 22} x2={cx} y2={cy - 82}
              stroke="#7b8ca5" strokeWidth="1.25" strokeLinecap="round" />
        <circle cx={cx} cy={cy + 16} r="3" fill="#7b8ca5" />
      </g>

      {/* Center pivot */}
      <circle cx={cx} cy={cy} r="3.5" fill="#1a1f2e" />
      <circle cx={cx} cy={cy} r="1.25" fill="#f5f7fa" />
    </svg>
  )
}
